import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Product } from '../types';

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  product: Product;
  quantity: number;
  tags: string[];
  isLoading?: boolean;
  processingMessage?: string;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  product,
  quantity,
  tags,
  isLoading = false,
  processingMessage = ''
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-secondary-400 hover:text-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={onClose}
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-warning-100 sm:mx-0 sm:h-10 sm:w-10">
              <ExclamationTriangleIcon className="h-6 w-6 text-warning-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-secondary-900">
                Verify Tag Match
              </h3>
              <div className="mt-4">
                <p className="text-sm text-secondary-600 mb-4">
                  Please verify the following information before confirming the tag match:
                </p>
                
                <div className="bg-secondary-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-secondary-700">Product:</p>
                    <p className="text-sm text-secondary-900">{product.itemNumber}</p>
                    <p className="text-sm text-secondary-600">{product.description}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-secondary-700">Package Quantity:</p>
                    <p className="text-sm text-secondary-900">{quantity} item{quantity !== 1 ? 's' : ''} per tag</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-secondary-700">Tags to Match:</p>
                    <div className="mt-1 space-y-1">
                      {tags.map((tag, index) => (
                        <p key={index} className="text-sm text-secondary-900 font-mono bg-white px-2 py-1 rounded">
                          {tag}
                        </p>
                      ))}
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t border-secondary-200">
                    <p className="text-sm font-medium text-secondary-700">Total Items:</p>
                    <p className="text-sm text-secondary-900">
                      {tags.length} tag{tags.length !== 1 ? 's' : ''} × {quantity} item{quantity !== 1 ? 's' : ''} = {tags.length * quantity} total item{tags.length * quantity !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:text-sm"
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:text-sm"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {processingMessage || 'Processing...'}
                </>
              ) : (
                'Confirm'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;